/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { RefreshCw, Home } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);
  }

  public handleRetry = () => {
    this.setState({ hasError: false, error: null });
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="w-full min-h-[400px] flex flex-col items-center justify-center p-6 text-center bg-white/90 rounded-[2.5rem] border-4 border-amber-200 shadow-lg max-w-lg mx-auto my-8">
          <span className="text-6xl mb-4 animate-bounce">🎈</span>
          <h2 className="text-2xl font-black text-navy mb-2">Oops! Let's Try Again!</h2>
          <p className="text-sm font-medium text-slate-600 mb-6 max-w-xs">
            We had a small hiccup loading this fun activity. Tap below to reload it!
          </p>
          <div className="flex gap-3 flex-wrap justify-center">
            <button
              onClick={this.handleRetry}
              className="px-6 py-3 bg-amber-400 hover:bg-amber-500 text-amber-950 font-black rounded-2xl border-b-4 border-amber-600 transition-all cursor-pointer flex items-center gap-2 active:translate-y-[1px]"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Retry Activity</span>
            </button>
            {this.props.onReset && (
              <button
                onClick={this.props.onReset}
                className="px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-black rounded-2xl transition-all cursor-pointer flex items-center gap-2 active:translate-y-[1px]"
              >
                <Home className="w-4 h-4" />
                <span>Go to Home</span>
              </button>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
